export const PROJECTS = [
  {
    slug: "orchestriq",
    name: "OrchestrIQ",
    tagline: "Engineering Intelligence for Predictable Software Delivery",
    year: "2025",
    role: "Founder & Lead Engineer",
    summary:
      "Built for engineering leaders who need to know, with confidence, whether they will ship on time. OrchestrIQ turns scattered delivery data into a clear, trustworthy view of risk, so teams catch slippage earlier and plan with evidence. Built with React and a deterministic analytics engine paired with carefully grounded AI.",
    cover: "/projects/orchestriq-cover.jpg",
    liveDemo: "https://orchestr-iq.vercel.app",
    github: "https://github.com/vivek1389",
    tech: ["React", "TypeScript", "Node.js", "PostgreSQL", "LLM APIs", "Vercel"],
    overview:
      "OrchestrIQ is an engineering intelligence platform for software teams and the people who lead them. It combines dependable engineering metrics with short, AI-written executive summaries, so the people doing the work and the people funding it share the same trustworthy picture of delivery health.",
    problem:
      "Engineering leaders are constantly asked a simple question they struggle to answer with confidence: will we ship on time? The data needed to answer it sits scattered across issue trackers, pull requests and CI systems. By the time risks surface in a status meeting it is usually too late to act, and planning becomes guesswork instead of evidence.",
    solution:
      "OrchestrIQ brings that scattered data into one place and turns it into two layers of insight. First, metrics that leaders can trust and audit. Second, a short written summary that explains what those numbers mean for delivery. The result is a single source of truth for delivery risk that engineers and executives both believe.",
    architecture: [
      "A data layer that pulls and normalises signals from issue trackers and version control.",
      "A deterministic analytics engine that computes throughput, cycle time and clear risk indicators.",
      "An AI summary layer that is only allowed to describe numbers the system has already calculated.",
      "A fast React dashboard served from cached, read-optimised data.",
    ],
    decisions: [
      {
        title: "Deterministic first, AI second",
        body: "Every metric is calculated deterministically, so leaders can trust and audit it. The AI only narrates verified numbers, it never invents them. In an executive setting, that distinction is the difference between a tool people rely on and one they quietly stop opening.",
      },
      {
        title: "Insight over dashboards",
        body: "Instead of another wall of charts, the product leads with the answer: what is at risk, and why. Charts are there to support the thinking, not to replace it.",
      },
    ],
    challenges:
      "The hardest problem was trust. A single fabricated number in an executive summary would undermine the whole product. The fix was strict grounding. The model receives only structured, pre-calculated facts, and every figure shown on screen comes from the source data rather than from the model's text.",
    outcome:
      "OrchestrIQ gives teams earlier warning when delivery is slipping, and gives leadership summaries they can act on. Conversations shift from reporting what already happened to preventing problems before they land, which is where good planning actually pays off.",
    lessons:
      "The most useful AI features are often the most constrained. Grounding the model in real data is what turned this from a novelty into something teams trust. Reliability is not a happy accident, it is something you design for on purpose.",
    gallery: [
      "/projects/orchestriq-s1.jpg",
      "/projects/orchestriq-s2.jpg",
      "/projects/orchestriq-s3.jpg",
    ],
  },
  {
    slug: "ichi-and-ori-pet-care",
    name: "Ichi & Ori Pet Care",
    tagline: "The Digital Platform for a Premium Veterinary Clinic",
    year: "2024",
    role: "Design & Engineering",
    summary:
      "Built for a premium veterinary clinic that had no way to be found or booked online. The new platform gives pet owners a trustworthy first impression and a simple path to book, while ranking for the searches that bring in new customers. Delivered as a fast, SEO-first React site.",
    cover: "/projects/ichiandori-cover.jpg",
    liveDemo: "https://www.ichiandoripetcare.com",
    github: null,
    tech: ["React", "Tailwind CSS", "SEO", "Headless Content", "Vercel"],
    overview:
      "Ichi & Ori is a premium veterinary clinic. I designed and built their entire digital presence, from the website and its content to the booking journey and the SEO strategy that helps new customers find them. The goal was a digital experience that matches the quality of care the clinic is known for in person.",
    problem:
      "A premium clinic was effectively invisible online. Pet owners increasingly search and book on the web, but the business had no proper website, no visibility in search and no simple way for a visitor to take the next step. Every day without that presence meant new customers going to competitors who were easier to find.",
    solution:
      "I built a content-rich, trustworthy website with a clear path to booking an appointment. Educational content answers the questions pet owners actually search for, which builds authority and brings in organic traffic. The information is organised so a first-time visitor is guided naturally from curiosity to enquiry.",
    architecture: [
      "A component-driven React front end tuned for Core Web Vitals and fast mobile loading.",
      "An SEO-first content structure for services and educational articles.",
      "A clear booking journey with calls to action placed throughout the site.",
      "Static-first delivery for speed, reliability and almost no ongoing maintenance.",
    ],
    decisions: [
      {
        title: "Experience as the brand",
        body: "For a premium clinic, the website is part of the product. Every screen was designed to feel calm, trustworthy and clearly high-end, because that is what reassures a worried pet owner.",
      },
      {
        title: "SEO as a growth channel",
        body: "The content was structured to rank for the real questions pet owners ask. That turns the site into a durable source of new customers rather than a one-off brochure.",
      },
    ],
    challenges:
      "The site had to feel rich and editorial while still loading quickly on a phone, which is where most pet owners browse. That meant treating imagery and performance as a single problem rather than competing goals.",
    outcome:
      "The clinic now has a professional presence that reinforces its premium positioning, is far easier to find in search and gives customers a simple way to book. It works as a dependable source of new business rather than a digital afterthought.",
    lessons:
      "For local, service-based businesses, a trustworthy experience and steady SEO compound over time. The website is not decoration. It is often the most reliable salesperson the business has.",
    gallery: [
      "/projects/ichiandori-s1.jpg",
      "/projects/ichiandori-s2.jpg",
      "/projects/ichiandori-s3.jpg",
    ],
  },
  {
    slug: "truequal-dataworks",
    name: "TrueQual Dataworks",
    tagline: "A Modern Software Consultancy, Positioned to Win Trust",
    year: "2024",
    role: "Founder",
    summary:
      "The digital home of my software consultancy, built to earn the trust of founders and CTOs within seconds. Clear positioning around business outcomes and visible engineering craft help the company feel established from the first visit. Delivered as a fast, static React site.",
    cover: "/projects/truequal-cover.jpg",
    liveDemo: "https://truequaldataworks.com",
    github: null,
    tech: ["React", "Tailwind CSS", "Design Systems", "SEO", "Vercel"],
    overview:
      "TrueQual Dataworks is my software consultancy. I designed its identity and built its website to position it as a modern software partner for startups and established businesses. Because a consultancy is judged on trust, the site had to earn credibility with both technical and commercial audiences within seconds.",
    problem:
      "A new consultancy has no track record to point to, yet it has to look established from day one. The challenge was to stand clearly apart from freelancers and generic agencies, and to signal quickly that this is a serious partner you would trust with important software.",
    solution:
      "I led with positioning before design: outcomes over technologies, partner over vendor. From there I built a restrained, premium website where the craft itself is part of the argument. A founder and a careful CTO should both leave convinced within a single visit.",
    architecture: [
      "A small design system defining typography, spacing and a restrained colour language.",
      "A reusable React component library for consistent, maintainable pages.",
      "An SEO and metadata foundation for discoverability.",
      "Static delivery for speed and effortless maintenance.",
    ],
    decisions: [
      {
        title: "Positioning before pixels",
        body: "The message came first: what TrueQual does, and for whom. Design decisions followed from that positioning rather than from trends, which keeps the site clear and confident.",
      },
      {
        title: "Craft as proof",
        body: "For a software consultancy, the website is the portfolio. Every detail is evidence of the standard a client can expect, so the quality bar was treated as non-negotiable.",
      },
    ],
    challenges:
      "The hard part was communicating depth without overwhelming non-technical decision makers. The site needed to satisfy a careful CTO and a busy founder in the same visit, which meant editing hard and saying less.",
    outcome:
      "TrueQual launched with a confident, premium identity that positions it as a modern software partner. It gives the business a credible foundation for winning client engagements and consulting work.",
    lessons:
      "Clear positioning is worth more than visual novelty. When the message is sharp, restrained design lands harder and builds trust faster than anything decorative.",
    gallery: [
      "/projects/truequal-s1.jpg",
      "/projects/truequal-s2.jpg",
    ],
  },
];

export const getProject = (slug) => PROJECTS.find((p) => p.slug === slug);
